import TenantContext from '@aurora/shared-client/components/context/TenantContext';
import React, { useContext } from 'react';
import localStyles from './ChapterItem.module.pcss';
import { Accordion, useClassNameMapper } from 'react-bootstrap';
import ArticleLink, { LinkStyleType } from '../ArticleLink/ArticleLink';
import GuideAccordionToggle from '../GuideAccordionToggle/GuideAccordionToggle';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import chapterQuery from '../../GuideContentManagement/Chapter.query.graphql';
import type {
  ChapterQuery,
  ChapterQueryVariables,
  GuideContentChapterViewFragment
} from '../../../../types/graphql-types';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import { EndUserQueryParams } from '@aurora/shared-types/pages/enums';

/**
 * Props for the ChapterItem component.
 */
interface Props {
  /**
   * The chapter node
   */
  chapter: GuideContentChapterViewFragment;

  /**
   * A boolean indicating whether the chapter item is active or not.
   */
  isActive: boolean;

  /**
   * A function to toggle the active state of the chapter item.
   * @param id - The ID of the chapter item to toggle.
   */
  toggleItem: (id: string) => void;

  /**
   * The ID of the selected guide associated with the article.
   */
  selectedGuideId: string;
}

/**
 * Guide item element
 *
 * @author Amit Agrawal
 */
const ChapterItem: React.FC<React.PropsWithChildren<Props>> = ({
  chapter,
  isActive,
  toggleItem,
  selectedGuideId
}) => {
  const cx = useClassNameMapper(localStyles);
  const {
    contextMessage: { id: contextMessageId, uid, board }
  } = useContext(AppContext);

  const {
    publicConfig: { maxGuidesAndContentLimit }
  } = useContext(TenantContext);

  const { data } = useQueryWithTracing<ChapterQuery, ChapterQueryVariables>(module, chapterQuery, {
    variables: {
      id: chapter.id,
      first: maxGuidesAndContentLimit
    },
    skip: !isActive
  });

  const chapterArticles = data?.chapter?.articles;
  const guideId = data?.chapter?.guide.id;

  return (
    <Accordion
      key={chapter.id}
      className={cx('lia-chapter')}
      activeKey={isActive ? chapter.id : undefined}
    >
      <GuideAccordionToggle
        id={chapter.id}
        title={chapter.title}
        isOpen={isActive}
        onClick={async () => {
          toggleItem(chapter.id);
        }}
      >
        {chapterArticles &&
          chapterArticles.edges.map(({ node: article }) => (
            <ArticleLink
              key={article.id}
              article={{ ...article.message, board, uid }}
              selectedGuideId={selectedGuideId}
              parentGuideId={guideId}
              useShallow={true}
              linkStyleType={LinkStyleType.CONTENT}
              contextMessageId={contextMessageId}
              additionalQueryParams={{
                [EndUserQueryParams.NODE_ID]: article.message.board.id,
                [EndUserQueryParams.ARTICLE_ID]: article.message.id
              }}
            />
          ))}
      </GuideAccordionToggle>
    </Accordion>
  );
};

export default ChapterItem;
